import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';
import { HomeDetails } from '../models/home.details.interface';
import { ListSummary, GenericModelList } from '../models/list.summary.interface';
import { AdminGenericEdit } from '../models/admin.edit.interface';

import { Office } from '../../shared/models/office.interface';
import { Vendor } from '../../shared/models/vendor.interface';
import { RolePermissions } from '../../shared/models/rolePermissions.interface';
import { GenericPageInfo } from '../models/generic.pageinfo.interface';
import { GenericPageTypes } from '../enums/generic-page-types.enum';
import { Area } from '../../shared/models/area.interface';
import { SubArea } from '../../shared/models/subarea.interface';
import { IncomeAccount } from '../../shared/models/income.account.interface';
import { VendorType } from '../../shared/models/vendor.type.interface';
import { Warehouse } from '../../shared/models/warehouse.interface';
import { WarehouseLocation } from '../../shared/models/warehouse.location.interface';
import { Resource } from '../../shared/models/resource.interface';
import { ResourceType } from '../../shared/models/resource.type.interface';
import { Crew } from '../../shared/models/crew.interface';
import { CrewPersonnel } from '../../shared/models/crew.personnel.interface';
import { ItemType, ItemCoverageTypes } from '../../shared/models/item.type.interface';
import { ItemMaster } from '../../shared/models/item.master.interface';
import { RValue } from '../../shared/models/r.value.interface';
import { ItemUsage } from '../../shared/models/item.usage.interface';
import { Role } from '../../shared/models/role.interface';
import { User } from '../../shared/models/user.interface';
import { ItemBulkUpdate } from '../models/item.bulk.update.interface';
import { ItemFilter } from '../models/item.filter';
import { AdminSetup } from '../models/admin.setup.interface';
import { IdiItemSource } from '../../shared/models/idiitem.source.interface';
import { Dealer } from '../../shared/models/dealer.interface';
import { BulkUpdateChanges } from '../models/bulk.update.reset.interface';
import { IdiAreaSource } from '../../shared/models/Idiarea.source.interface';
import { IdiSubareaSource } from '../../shared/models/idisubarea.source.interface';
import { FieldTechnician } from '../../shared/models/field.technician.interface';
import { DealerDocument } from '../../shared/models/dealer.document.interface';
import { FileUpload } from '../../shared/models/file.upload.interface';
import { ImportItemQtyData, ImportItemQtyErrors } from '../item/models/import-item-qty.interface';
import { ImportExtermalItemErrors, ImportExternalItemData } from '../item/models/import-external-item.interface';
import { IDIItemSourceList } from '../models/idiItemSourceList.interface';
import { PushItems } from '../../shared/models/pushItems.interface';

@Injectable()

export class AdminService extends BaseService {

  constructor(private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getHomeDetails(): Observable<HomeDetails> {
    return this.httpGet<HomeDetails>("/admin/home");
  }

  checkDealerSetup(): Observable<AdminSetup> {
    return this.httpGet<AdminSetup>("/admin/checkSetup");
  }

  getDealers(): Observable<Dealer[]> {
    return this.httpGet<Dealer[]>("/dealer");
  }

  getList(page: string, dealerId: number): Observable<GenericModelList[]> {
    let url = "/admin/" + page;
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<GenericModelList[]>(url);
  }

  getEntity(page: string, id: number): Observable<AdminGenericEdit> {
    return this.httpGet<AdminGenericEdit>(`/admin/${page}/${id}`);
  }

  saveEntity(entityModel: AdminGenericEdit, page: string, dealerId: number): Observable<number> {
    let url = "/admin/" + page;

    if (entityModel.id != null) url += "/" + entityModel.id;
    if (dealerId) url += '?dealerId=' + dealerId;

    if (entityModel.id == null)
      return this.httpPost<number>(url, entityModel);
    else
      return this.httpPut<number>(url, entityModel);
  }

  toggleStatus(page: string, id: number, dealerId: number): Observable<AdminGenericEdit> {
    let url = "/admin/toggle/" + page + "/" + id;
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpPut<AdminGenericEdit>(url, null);
  }

  toggleUserStatus(page: string, id: string, dealerId: number): Observable<User> {
    let url = "/user/toggle/" + id;
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpPut<User>(url, null);
  }

  setPrimaryOffice(id: number, dealerId: number): Observable<number> {
    let url = "/office/" + id + "/primaryContact/";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpPut<number>(url, null);
  }

  resequenceAreas(dealerId: number, areas: Area[]): Observable<number> {
    let url = `/area/resequenceAreas`;
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpPut<number>(url, areas);
  }

  resequenceSubAreas(areaId: number, dealerId: number, subAreas: SubArea[]): Observable<number> {
    let url = `/area/${areaId}/resequenceSubAreas`;
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpPut<number>(url, subAreas);
  }

  getAccounts(dealerId: number): Observable<IncomeAccount[]> {
    let url = "/admin/incomeaccount";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<IncomeAccount[]>(url);
  }

  /* Area section*/
  getAreaList(dealerId: number): Observable<Area[]> {
    let url = "/area";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<Area[]>(url);
  }
  getArea(areaId: number): Observable<Area> {
    return this.httpGet<Area>("/area/" + areaId);
  }

  saveArea(area: Area): Observable<number> {
    if (area.area_ID == null)
      return this.httpPost<number>("/area/", area);
    else
      return this.httpPut<number>(`/area/${area.area_ID}`, area);
  }

  saveAreaAccessories(areaId: number, areaItems: number[]): Observable<number> {
    return this.httpPut<number>(`/area/${areaId}/accessories`, areaItems);
  }

  getSubAreas(areaId): Observable<SubArea[]> {
    return this.httpGet<SubArea[]>(`/area/${areaId}` + "/subarea");
  }

  getSubArea(subAreaId): Observable<SubArea> {
    return this.httpGet<SubArea>("/area/subarea/" + subAreaId);
  }

  saveSubArea(subArea: SubArea): Observable<number> {
    if (subArea.subArea_ID == null || subArea.subArea_ID == 0)
      return this.httpPost<number>(`/area/${subArea.area_ID}/subarea`, subArea);
    else
      return this.httpPut<number>(`/area/subarea/${subArea.subArea_ID}`, subArea);
  }

  /* IDI Area Source section*/
  getIDIAreaList(): Observable<IdiAreaSource[]> {
    return this.httpGet<IdiAreaSource[]>("/IDIarea");
  }
  getIDIArea(areaId: number): Observable<IdiAreaSource> {
    return this.httpGet<IdiAreaSource>("/IDIarea/" + areaId);
  }

  saveIDIArea(area: IdiAreaSource): Observable<number> {
    if (area.area_ID == null)
      return this.httpPost<number>("/IDIarea/", area);
    else
      return this.httpPut<number>("/IDIarea/" + area.area_ID, area);
  }

  saveIDIAreaAccessories(areaId: number, areaItems: number[]): Observable<number> {
    return this.httpPut<number>(`/IDIarea/${areaId}/accessories`, areaItems);
  }

  getIDISubAreas(areaId): Observable<IdiSubareaSource[]> {
    return this.httpGet<IdiSubareaSource[]>(`/IDIarea/${areaId}/subarea`);
  }

  getIDISubArea(subAreaId): Observable<IdiSubareaSource> {
    return this.httpGet<IdiSubareaSource>("/IDIarea/subarea/" + subAreaId);
  }

  saveIDISubArea(subArea: IdiSubareaSource): Observable<number> {
    if (subArea.subArea_ID == null || subArea.subArea_ID == 0)
      return this.httpPost<number>(`/IDIarea/${subArea.area_ID}/subarea`, subArea);
    else
      return this.httpPut<number>(`/IDIarea/subarea/${subArea.subArea_ID}`, subArea);
  }
  /* end Area Source section*/

  /* Crew section*/
  getCrew(crewId): Observable<Crew> {
    return this.httpGet<Crew>("/crew/" + crewId);
  }

  saveCrew(crew: Crew): Observable<number> {
    if (crew.crew_ID == null)
      return this.httpPost<number>("/crew/", crew);
    else
      return this.httpPut<number>("/crew/" + crew.crew_ID, crew);
  }

  getFieldTechs(dealerId: number): Observable<User[]> {
    let url = "/crew/fieldTechs";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<User[]>(url);
  }

  getCrewPersonnel(crewId): Observable<CrewPersonnel[]> {
    return this.httpGet<SubArea[]>("/crew/" + crewId + "/crewpersonnel");
  }

  saveCrewPersonnel(crewId, crewPersonnel: CrewPersonnel): Observable<number> {
    if (crewPersonnel.crewPersonnel_ID == null || crewPersonnel.crewPersonnel_ID == 0)
      return this.httpPost<number>("/crew/" + crewId + "/crewpersonnel", crewPersonnel);
    else
      return this.httpPut<number>("/crew/crewpersonnel/" + crewPersonnel.crewPersonnel_ID, crewPersonnel);
  }

  /*Dealer Documents Section */
  getDocument(documentId): Observable<DealerDocument> {
    return this.httpGet<DealerDocument>("/document/" + documentId);
  }

  saveDocument(document: DealerDocument): Observable<number> {
    if (document.dealerDocument_ID == null)
      return this.httpPost<number>("/document/", document);
    else
      return this.httpPut<number>("/document/" + document.dealerDocument_ID, document);
  }

  getDocumentAttachment(documentId: number): Observable<Blob> {
    return this.httpGetFile("/document/attachment/" + documentId);
  }

  /* item master section*/
  getItemList(): Observable<ItemMaster[]> {
    return this.httpGet<ItemMaster[]>("/item");
  }

  exportItemList(itemFilter: ItemFilter, dealerId: number): Observable<Blob> {
    let params = new HttpParams();

    if (itemFilter.itemTypeId) params = params.append('itemTypeId', itemFilter.itemTypeId.toString());
    if (itemFilter.rValueId) params = params.append('rValue', itemFilter.rValueId.toString());
    if (itemFilter.searchText) params = params.append('searchText', itemFilter.searchText);
    if (itemFilter.statusId != null) params = params.append('statusId', (itemFilter.statusId == 1).toString());
    if (dealerId != null) params = params.append('dealerId', dealerId.toString());

    return this.httpGetFile("/item/export?" + params.toString());
  }

  searchItemList(itemFilter: ItemFilter, dealerId: number, sortColumn: string = null, sortDirection: string = null): Observable<ItemMaster[]> {
    let params = new HttpParams();

    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (itemFilter.itemTypeId) params = params.append('itemTypeId', itemFilter.itemTypeId.toString());
    if (itemFilter.rValueId) params = params.append('rValue', itemFilter.rValueId.toString());
    if (itemFilter.searchText) params = params.append('searchText', itemFilter.searchText);
    if (itemFilter.statusId != null) params = params.append('statusId', (itemFilter.statusId == 1).toString());
    if (dealerId != null) params = params.append('dealerId', dealerId.toString());

    return this.httpGet<ItemMaster[]>("/item/search?" + params.toString());
  }

  deleteItem(itemId): Observable<number> {
    return this.httpDelete<number>("/item/" + itemId);
  }

  getItem(itemId): Observable<ItemMaster> {
    return this.httpGet<ItemMaster>("/item/" + itemId);
  }

  saveItem(item: ItemMaster): Observable<number> {
    if (item.item_ID == null)
      return this.httpPost<number>("/item/", item);
    else
      return this.httpPut<number>("/item/" + item.item_ID, item);
  }

  importItemQty(importFile: FileUpload): Observable<ImportItemQtyData[]> {
    return this.httpPost<ImportItemQtyData[]>("/item/import", importFile);
  }

  itemBulkUpdate(itemBulkUpdate: ItemBulkUpdate): Observable<number> {
    return this.httpPut<number>("/item/bulkUpdate", itemBulkUpdate);
  }

  getBulkChanges(dealerId: number): Observable<BulkUpdateChanges[]> {
    return this.httpGet<BulkUpdateChanges[]>("/item/bulkUpdateChanges" + '?dealerId=' + dealerId);
  }

  rollbackBulkChange(bulkChangeId: number): Observable<number> {
    return this.httpPut<number>("/item/rollbackBulkChanges" + '?bulkChangeId=' + bulkChangeId, null);
  }

  checkCanDeleteItem(itemId): Observable<boolean> {
    return this.httpGet<boolean>("/item/canDeleteItem/" + itemId);
  }

  saveItemCoverages(itemId: number, itemUsages: ItemUsage[]): Observable<number> {
    return this.httpPost<ItemUsage[]>("/item/" + itemId + "/itemcoverage", itemUsages);
  }

  getItemTypes(dealerId: number): Observable<ItemType[]> {
    let url = "/item/itemtype";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<ItemType[]>(url);
  }

  getItemType(itemTypeId): Observable<ItemType> {
    return this.httpGet<ItemType>("/item/itemtype/" + itemTypeId);
  }

  getItemCoverageTypes(): Observable<ItemCoverageTypes[]> {
    return this.httpGet<ItemCoverageTypes[]>("/item/itemcoveragetypes/");
  }

  saveItemType(itemType: ItemType): Observable<number> {
    if (itemType.itemType_ID == null)
      return this.httpPost<number>("/item/itemtype", itemType);
    else
      return this.httpPut<number>("/item/itemtype/" + itemType.itemType_ID, itemType);
  }

  getrValues(): Observable<RValue[]> {
    return this.httpGet<RValue[]>("/item/rValue");
  }

  /*idi item source section*/
  searchIDIItemList(itemFilter: ItemFilter): Observable<IdiItemSource[]> {
    let params = new HttpParams();

    if (itemFilter.itemTypeId) params = params.append('itemTypeId', itemFilter.itemTypeId.toString());
    if (itemFilter.rValueId) params = params.append('rValue', itemFilter.rValueId.toString());
    if (itemFilter.searchText) params = params.append('searchText', itemFilter.searchText);
    if (itemFilter.statusId != null) params = params.append('statusId', (itemFilter.statusId == 1).toString());

    return this.httpGet<IdiItemSource[]>("/idiItem/search?" + params.toString());
  }

  /*idi item source section*/
  searchIDIItemSourceList(itemFilter: ItemFilter): Observable<IDIItemSourceList[]> {
    let params = new HttpParams();

    if (itemFilter.itemTypeId) params = params.append('itemTypeId', itemFilter.itemTypeId.toString());
    if (itemFilter.rValueId) params = params.append('rValue', itemFilter.rValueId.toString());
    if (itemFilter.searchText) params = params.append('searchText', itemFilter.searchText);
    if (itemFilter.statusId != null) params = params.append('statusId', (itemFilter.statusId == 1).toString());

    return this.httpGet<IDIItemSourceList[]>("/idiItem/searchList?" + params.toString());
  }

  deleteIDIItem(itemId): Observable<number> {
    return this.httpDelete<number>("/idiItem/" + itemId);
  }

  getIDIItem(itemId): Observable<IdiItemSource> {
    return this.httpGet<IdiItemSource>("/idiItem/" + itemId);
  }

  saveIDIItem(item: IdiItemSource): Observable<number> {
    if (item.item_ID == null)
      return this.httpPost<number>("/idiItem/", item);
    else
      return this.httpPut<number>("/idiItem/" + item.item_ID, item);
  }

  idiItemBulkUpdate(itemBulkUpdate: ItemBulkUpdate): Observable<number> {
    return this.httpPut<number>("/idiItem/bulkUpdate", itemBulkUpdate);
  }

  pushIDIItemsToDealers(pushRequest: PushItems): Observable<number> {
    return this.httpPut<number>(`/idiItem/pushIDIItem`, pushRequest);
  }

  pushIDIItemsTypeToDealers(itemTypeId: number): Observable<number> {
    return this.httpPut<number>(`/idiItem/pushIDIItemType`, itemTypeId);
  }


  exportItemSourceList(itemFilter: ItemFilter, dealerId: number): Observable<Blob> {
    let params = new HttpParams();

    if (itemFilter.itemTypeId) params = params.append('itemTypeId', itemFilter.itemTypeId.toString());
    if (itemFilter.rValueId) params = params.append('rValue', itemFilter.rValueId.toString());
    if (itemFilter.searchText) params = params.append('searchText', itemFilter.searchText);
    if (itemFilter.statusId != null) params = params.append('statusId', (itemFilter.statusId == 1).toString());
    if (dealerId != null) params = params.append('dealerId', dealerId.toString());

    return this.httpGetFile("/idiItem/export?" + params.toString());
  }

  importExternalItemSource(importFile: FileUpload): Observable<ImportExternalItemData[]> {
    return this.httpPost<ImportExternalItemData[]>("/idiItem/importItems", importFile);
  }

  exportItemImportErrorsList(failedRequest: ImportExternalItemData[]): Observable<Blob> {
    return this.httpPostFile(`/idiItem/export/importItemErrors`, failedRequest);
  }
  exportItemQtyErrorsList(failedRequest: ImportItemQtyErrors): Observable<Blob> {
    return this.httpPostFile(`/item/export/importQtyErrors`, failedRequest);
  }

  /* end idi item source */



  getOffice(officeId): Observable<Office> {
    return this.httpGet<Office>("/office/" + officeId);
  }

  saveOffice(office: Office): Observable<number> {
    if (office.office_ID == null)
      return this.httpPost<number>("/office/", office);
    else
      return this.httpPut<number>("/office/" + office.office_ID, office);
  }

  getResource(resourceId): Observable<Resource> {
    return this.httpGet<Resource>("/resource/" + resourceId);
  }

  saveResource(resource: Resource): Observable<number> {
    if (resource.resource_ID == null)
      return this.httpPost<number>("/resource/", resource);
    else
      return this.httpPut<number>("/resource/" + resource.resource_ID, resource);
  }

  getFieldTechnician(fieldTechnicianId): Observable<FieldTechnician> {
    return this.httpGet<FieldTechnician>("/crew/fieldTechs/" + fieldTechnicianId);
  }

  saveFieldTechnician(fieldTechnician: FieldTechnician): Observable<number> {
    if (fieldTechnician.fieldTechnician_ID == null)
      return this.httpPost<number>("/crew/fieldTechs/", fieldTechnician);
    else
      return this.httpPut<number>("/crew/fieldTechs/" + fieldTechnician.fieldTechnician_ID, fieldTechnician);
  }

  getResourceTypes(dealerId: number): Observable<ResourceType[]> {
    let url = '/resource/types';
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<ResourceType[]>(url);
  }

  getAvailableRoles(dealerId: number): Observable<Role[]> {
    let url = '/role';
    if (dealerId) url += '?dealerId=' + dealerId;
    return this.httpGet<Role[]>(url);
  }

  getAvailableOffices(dealerId: number): Observable<Office[]> {
    let url = "/office";
    if (dealerId) url += '?dealerId=' + dealerId;
    return this.httpGet<Office[]>(url);
  }

  getRole(roleId): Observable<Role> {
    return this.httpGet<Role>("/role/" + roleId);
  }

  getRolePermissions(roleId: number): Observable<RolePermissions[]> {
    return this.httpGet<RolePermissions[]>("/role/" + roleId + "/permissions");
  }

  saveRole(role: Role): Observable<number> {
    if (role.role_ID == null)
      return this.httpPost<number>("/role/", role);
    else
      return this.httpPut<number>("/role/" + role.role_ID, role);
  }

  saveRolePermissions(roleId, rolePermissions: number[]): Observable<string> {
    return this.httpPut<string>("/role/" + roleId + "/permissions", rolePermissions);
  }

  getStates(): Observable<string[]> {
    return this.httpGet<string[]>("/admin/states");
  }

  getVendorsList(dealerId: number): Observable<Vendor[]> {
    let url = "/vendor";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<Vendor[]>(url);
  }

  getVendor(vendorId): Observable<Vendor> {
    return this.httpGet<Vendor>("/vendor/" + vendorId);
  }

  getVendorTypes(dealerId: number): Observable<VendorType[]> {
    let url = "/vendor/types";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<VendorType[]>(url);
  }

  saveVendor(vendor: Vendor): Observable<number> {
    if (vendor.vendor_ID == null)
      return this.httpPost<number>("/vendor/", vendor);
    else
      return this.httpPut<number>("/vendor/" + vendor.vendor_ID, vendor);
  }

  getWarehouseList(dealerId: number): Observable<Warehouse[]> {
    let url = "/warehouse";
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpGet<Warehouse[]>(url);
  }

  getWarehouse(warehouseId): Observable<Warehouse> {
    return this.httpGet<Warehouse>("/warehouse/" + warehouseId);
  }

  saveWarehouse(warehouse: Warehouse): Observable<Warehouse> {
    if (warehouse.warehouse_ID == null)
      return this.httpPost<Warehouse>("/warehouse/", warehouse);
    else
      return this.httpPut<Warehouse>("/warehouse/" + warehouse.warehouse_ID, warehouse);
  }

  getWarehouseLocations(warehouseId): Observable<WarehouseLocation[]> {
    return this.httpGet<WarehouseLocation[]>("/warehouse/" + warehouseId + "/locations");
  }

  getWarehouseLocation(warehouseLocationId): Observable<WarehouseLocation> {
    return this.httpGet<WarehouseLocation>("/warehouse/location/" + warehouseLocationId);
  }

  saveWarehouseLocation(warhouseId, warehouseLocation: WarehouseLocation): Observable<number> {
    if (warehouseLocation.warehouseLocation_ID == null)
      return this.httpPost<number>("/warehouse/" + warhouseId + "/location", warehouseLocation);
    else
      return this.httpPut<number>("/warehouse/location/" + warehouseLocation.warehouseLocation_ID, warehouseLocation);
  }


  sendMessage(message: HomeDetails): Observable<boolean> {
    let result = new Subject<boolean>();

    return result.asObservable();
  }

  setGenericPageInfo(action: string, locale: string) {
    if (locale === 'es')
      return this.setGenericPageInfoEs(action);

    return this.setGenericPageInfoEn(action);
  }

  setGenericPageInfoEn(action: string) {
    let pageInfo = {} as GenericPageInfo;
    
    switch (action) {
      case GenericPageTypes.Account.toLowerCase():
      case GenericPageTypes.Account:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Account + 's';
        pageInfo.label = "Account Name";
        pageInfo.label2 = 'Account Number';
        pageInfo.label3 = 'Type of Account';
        pageInfo.listDescription = "Account Types are used to define income and expense accounts.";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Area.toLowerCase():
      case GenericPageTypes.Area:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Area + 's';
        pageInfo.label = "Area Name";
        pageInfo.listDescription = "Grouped locations you install insulation into a project (Ceiling, walls, floor, basement)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Competitor.toLowerCase():
      case GenericPageTypes.Competitor:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Competitor + 's';
        pageInfo.label = GenericPageTypes.Competitor;
        pageInfo.listDescription = "Who you lose opportunities to (company name, unknown)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Contact Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Contact Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Contact Type"] + 's';
        pageInfo.label = GenericPageTypes["Contact Type"];
        pageInfo.listDescription = "Groups of common contacts (customer “owner, accounting, manager, scheduler”, vendor, employee)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Crew.toLowerCase():
      case GenericPageTypes.Crew:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Crew + 's';
        pageInfo.label = GenericPageTypes.Crew;
        pageInfo.listDescription = "Crews are a group of users who typically work together and can be assigned to Work Orders";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Customer Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Customer Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Customer Type"] + 's';
        pageInfo.label = GenericPageTypes["Customer Type"];
        pageInfo.label2 = "Default Gross Margin";
        pageInfo.listDescription = "Groups of common customers (residential – new, custom tract, retro, commercial – new, retro)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Document.toLowerCase():
      case GenericPageTypes.Document:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Document + 's';
        pageInfo.label = GenericPageTypes.Document;
        pageInfo.listDescription = "Documents that can be included when sending quotes";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Field Technician"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Field Technician"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Field Technician"] + 's';
        pageInfo.label = GenericPageTypes["Field Technician"];
        pageInfo.listDescription = "Field Technicians are individuals that may be assigned to crews";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Item Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Item Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Item Type"] + 's';
        pageInfo.label = GenericPageTypes["Item Type"];
        pageInfo.listDescription = "Groups of materials you use (batts, blown, rigid, spray foam)";
        break;
      case GenericPageTypes["Job Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Job Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Job Type"] + 's';
        pageInfo.label = GenericPageTypes["Job Type"];
        pageInfo.listDescription = "Groups of similar projects (residential new or retro, commercial new or retro)";
        break;
      case GenericPageTypes["Lead Source"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Lead Source"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Lead Source"] + 's';
        pageInfo.label = GenericPageTypes["Lead Source"];
        pageInfo.listDescription = "Where your opportunities are coming from (referral, repeat customer, yellow pages, billboard, flyer, ad)";
        break;
      case GenericPageTypes["Lost Reason"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Lost Reason"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Lost Reason"] + 's';
        pageInfo.label = GenericPageTypes["Lost Reason"];
        pageInfo.listDescription = "Why you lose work (pricing, scheduling, location, cancelled, relationship)";
        break;
      case GenericPageTypes.Office.toLowerCase():
      case GenericPageTypes.Office:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Office.toString() + 's';;
        pageInfo.label = GenericPageTypes.Office;
        pageInfo.listDescription = "Unique locations within your company";
        break;
      case GenericPageTypes["Payment Term"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Payment Term"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Payment Term"] + 's';
        pageInfo.label = GenericPageTypes["Payment Term"];
        pageInfo.label2 = "Payment Term Days";
        pageInfo.listDescription = "How you expect to be paid (at time of work, invoiced)";
        break;
      case GenericPageTypes["Payment Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Payment Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Payment Type"] + 's';
        pageInfo.label = GenericPageTypes["Payment Type"];
        pageInfo.listDescription = "Methods of payment you accept (cash, check, credit card, PayPal)";
        break;
      case GenericPageTypes["Quote Template"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Quote Template"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Quote Template"] + 's';
        pageInfo.label = GenericPageTypes["Quote Template"];
        pageInfo.listDescription = "Templates are predefined areas and items that can be added to a new quote.";
        break;
      case GenericPageTypes["Quote Item Note Template"].replaceAll(" ", "").toLowerCase():
      case GenericPageTypes["Quote Item Note Template"].replaceAll(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Quote Item Note Template"] + 's';
        pageInfo.label = GenericPageTypes["Quote Item Note Template"];
        pageInfo.label2 = "Description";
        pageInfo.listDescription = "Quote Item Note Templates are predefined notes that can be added to a quote line item.";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Resource.toLowerCase():
      case GenericPageTypes.Resource:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Resource + 's'
        pageInfo.label = GenericPageTypes.Resource;
        pageInfo.listDescription = "The equipment you use to complete your work (Blow Truck #11)";
        break;
      case GenericPageTypes["Resource Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Resource Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Resource Type"] + 's';
        pageInfo.label = GenericPageTypes["Resource Type"];
        pageInfo.listDescription = "Types of equipment you use to complete your work (trucks, scaffolding, ladders)";
        break;
      case GenericPageTypes.Role.toLowerCase():
      case GenericPageTypes.Role:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Role + 's';
        pageInfo.label = GenericPageTypes.Role;
        pageInfo.listDescription = "Used to define permissions within BIDIT for each type of user (owner, manager, user)";
        break;
      case GenericPageTypes.User.toLowerCase():
      case GenericPageTypes.User:
        pageInfo.nextRoute = action;
        pageInfo.title = "Manage " + GenericPageTypes.User + 's';
        pageInfo.label = GenericPageTypes.User;
        pageInfo.listDescription = "Your employees who use BIDIT";
        break;
      case GenericPageTypes.Vendor.toLowerCase():
      case GenericPageTypes.Vendor:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Vendor + 's';
        pageInfo.label = GenericPageTypes.Vendor;
        pageInfo.listDescription = "Who you purchase goods or products from (suppliers)";
        break;
      case GenericPageTypes["Vendor Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Vendor Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Vendor Type"] + 's';
        pageInfo.label = GenericPageTypes["Vendor Type"];
        pageInfo.listDescription = "Vendor Types define the type of vendors used by your company.";
        break;
      case GenericPageTypes["Warehouse"].toLowerCase():
      case GenericPageTypes["Warehouse"]:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Warehouse"];
        pageInfo.label = "Warehouse Name";
        pageInfo.listDescription = "Locations you store inventory";
        break;
      case GenericPageTypes["Warehouse Location"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Warehouse Location"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Warehouse Location"];
        pageInfo.label = "Description";
        pageInfo.listDescription = "Bin, Shelf, Row  in the warehouse where inventory item ist stored";
        break;
      case GenericPageTypes["Work Order Status"].replace(/ /g, "").toLowerCase():
      case GenericPageTypes["Work Order Status"].replace(/ /g, ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Work Order Status"];
        pageInfo.label = "Description";
        pageInfo.listDescription = "Where work orders are in progress (quoted, approved, scheduled, active, complete)";
        break;
      case GenericPageTypes["Work Order Type"].replace(/ /g, "").toLowerCase():
      case GenericPageTypes["Work Order Type"].replace(/ /g, ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Work Order Type"] + 's';
        pageInfo.label = "Description";
        pageInfo.listDescription = "Groups of work orders you use to manage field crews (repair, rework, warranty)";
        break;
      default:
        pageInfo.nextRoute = action;
        break;
    }

    return pageInfo;
  }

  setGenericPageInfoEs(action: string) {
    let pageInfo = {} as GenericPageInfo;

    switch (action) {
      case GenericPageTypes.Account.toLowerCase():
      case GenericPageTypes.Account:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Account + 's';
        pageInfo.label = "Account Name";
        pageInfo.label2 = 'Account Number';
        pageInfo.label3 = 'Type of Account';
        pageInfo.listDescription = "Account Types are used to define income and expense accounts.";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Area.toLowerCase():
      case GenericPageTypes.Area:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Area + 's';
        pageInfo.label = "Area Name";
        pageInfo.listDescription = "Grouped locations you install insulation into a project (Ceiling, walls, floor, basement)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Competitor.toLowerCase():
      case GenericPageTypes.Competitor:
        pageInfo.nextRoute = action;
        pageInfo.title = 'Competidores';
        pageInfo.label = 'Competidor';
        pageInfo.listDescription = "Con quién perdio oportunidades (nombre de la empresa, desconosido)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Contact Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Contact Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipos de contacto';
        pageInfo.label = 'Tipo de contact';
        pageInfo.listDescription = "Grupos de contactos comunes (cliente, propietario, contabilidad, gerente, programador, proveedor, empleado)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Crew.toLowerCase():
      case GenericPageTypes.Crew:
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tripulaciones';
        pageInfo.label = 'Tripulación';
        pageInfo.listDescription = "Las cuadrills son un grupo de usuarios que no normalmente trabajan juntos y se pueden asignar a órdenes de trabajo.";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Customer Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Customer Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipo de clientes';
        pageInfo.label = 'Tipo de cliente';
        pageInfo.label2 = "Margen bruto predeterminado";
        pageInfo.listDescription = "Grupo de clientes comnes (residenciales-nuevos, tracto personalizado, retro, comerical-nuevo)";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes.Document.toLowerCase():
      case GenericPageTypes.Document:
        pageInfo.nextRoute = action;
        pageInfo.title = $localize`Documents`;
        pageInfo.label = $localize`Document`;
        pageInfo.listDescription = $localize`Documents that can be included when sending quotes`;
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Field Technician"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Field Technician"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Técnicos de Campo';
        pageInfo.label = 'Técnico de Campo';
        pageInfo.listDescription = "Los Técnicos de Campo son personal que pueden ser asignadas a tripulaciónes";
        pageInfo.editDescription = "";
        break;
      case GenericPageTypes["Item Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Item Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipos de articulos';
        pageInfo.label = 'Tipo de articulo';
        pageInfo.listDescription = "Grupos de materiales que utiliza (bloques, soplados, rigidos, espuma en aerosol)";
        break;
      case GenericPageTypes["Job Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Job Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipos de trabajo';
        pageInfo.label = 'Tipo de trabajo';
        pageInfo.listDescription = "Grupos de proyectos similares (residencial nuevo o retro, comercial nuevo o retro)";
        break;
      case GenericPageTypes["Lead Source"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Lead Source"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Fuentes de plomo';
        pageInfo.label = 'Fuentes de plomo';
        pageInfo.listDescription = "De dónde provienen sus oportunidades (referencia, cliente habitual, páginas amarillas, cartelera, volante, anuncio)";
        break;
      case GenericPageTypes["Lost Reason"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Lost Reason"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Razones perdidas';
        pageInfo.label = 'Razón perdida';
        pageInfo.listDescription = "Por qué pierde el trabajo (precio, programación, ubicación, cancelación, relación)";
        break;
      case GenericPageTypes.Office.toLowerCase():
      case GenericPageTypes.Office:
        pageInfo.nextRoute = action;
        pageInfo.title = 'Oficinas';
        pageInfo.label = 'Oficina';
        pageInfo.listDescription = "Ubicaciones únicas dentro de su empresa";
        break;
      case GenericPageTypes["Payment Term"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Payment Term"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Términos de pago';
        pageInfo.label = 'Término de pago';
        pageInfo.label2 = "Término de pago en Dias";
        pageInfo.listDescription = "Cómo espera que le paguen (en el momento del trabajo, facturado)";
        break;
      case GenericPageTypes["Payment Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Payment Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Formas de pago';
        pageInfo.label = 'Tipo de pago';
        pageInfo.listDescription = "Metodos de pago que acepta (efectivo, cheque, tarjeta de crédito, PayPal)";
        break;
      case GenericPageTypes["Quote Template"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Quote Template"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Plantillas de presupuesto';
        pageInfo.label = 'Plantilla de presupuesto';
        pageInfo.listDescription = "Las plantillas son áreas y articulos predefinidos que se pueden agregar a una nueva cotización.";
        break;
      case GenericPageTypes.Resource.toLowerCase():
      case GenericPageTypes.Resource:
        pageInfo.nextRoute = action;
        pageInfo.title = 'Recursos'
        pageInfo.label = 'Recurso';
        pageInfo.listDescription = "Tipos de equipo que utiliza para completar su trabajo (camiones, andamios, escaleras)";
        break;
      case GenericPageTypes["Resource Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Resource Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipos de recursos';
        pageInfo.label = 'Tipo de recurso';
        pageInfo.listDescription = "Tipos de equipo que utiliza para completar su trabajo (camiones, andamios, escaleras)";
        break;
      case GenericPageTypes.Role.toLowerCase():
      case GenericPageTypes.Role:
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes.Role + 's';
        pageInfo.label = GenericPageTypes.Role;
        pageInfo.listDescription = "Se utiliza para definir permisos dentro de BIDIT para cada tipo de usuario (propietario, administrador, usuario)";
        break;
      case GenericPageTypes.User.toLowerCase():
      case GenericPageTypes.User:
        pageInfo.nextRoute = action;
        pageInfo.title = "Administrae Usuarios";
        pageInfo.label = GenericPageTypes.User;
        pageInfo.listDescription = "Tus empleados que usan BIDIT";
        break;
      case GenericPageTypes.Vendor.toLowerCase():
      case GenericPageTypes.Vendor:
        pageInfo.nextRoute = action;
        pageInfo.title = 'Proveedores';
        pageInfo.label = 'Proveedor';
        pageInfo.listDescription = "A quién compra bienes o productos (vendedores)";
        break;
      case GenericPageTypes["Vendor Type"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Vendor Type"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipos de Proveedores';
        pageInfo.label = 'Tipo de Proveedor';
        pageInfo.listDescription = "Tipos de Proveedores define el tipo de proveedores utilizados por su empresa.";
        break;
      case GenericPageTypes["Warehouse"].toLowerCase():
      case GenericPageTypes["Warehouse"]:
        pageInfo.nextRoute = action;
        pageInfo.title = 'Almacén';
        pageInfo.label = "Nombre del Almacén";
        pageInfo.listDescription = "Ubicaciones en las que almacena el inventario";
        break;
      case GenericPageTypes["Warehouse Location"].replace(" ", "").toLowerCase():
      case GenericPageTypes["Warehouse Location"].replace(" ", ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Ubicaciones de Almacén';
        pageInfo.label = "Descripción";
        pageInfo.listDescription = "Bin, Shelf, Row  in the warehouse where inventory item ist stored";
        break;
      case GenericPageTypes["Work Order Status"].replace(/ /g, "").toLowerCase():
      case GenericPageTypes["Work Order Status"].replace(/ /g, ""):
        pageInfo.nextRoute = action;
        pageInfo.title = GenericPageTypes["Work Order Status"];
        pageInfo.label = "Description";
        pageInfo.listDescription = "Where work orders are in progress (quoted, approved, scheduled, active, complete)";
        break;
      case GenericPageTypes["Work Order Type"].replace(/ /g, "").toLowerCase():
      case GenericPageTypes["Work Order Type"].replace(/ /g, ""):
        pageInfo.nextRoute = action;
        pageInfo.title = 'Tipo de órdenes de trabajo';
        pageInfo.label = "Tipo de órdenes de trabajo";
        pageInfo.listDescription = "Grupo de órdenes de trabajo que utiliza para administrar equipos de campo (reparación, reelaboración, garantia)";
        break;
      default:
        pageInfo.nextRoute = action;
        break;
    }

    return pageInfo;
  }
}
